import { Box, Flex, HStack, Stack, Text, VStack } from "@chakra-ui/react";
import React, { useState } from "react";
import { FiEdit, FiTrash } from "react-icons/fi";
import APCommonDialog from "./modals/APCommonDialog";
import APIconButton from "./APIconButton";
import { Play } from "./APAssetAtlas";
import { useVocabIntl } from "../hooks/useVocabTranslation";

interface SenarioCardProps {
  name: string;
  tags: string[];
  id: string;
  onStart: (scenarioId: string, skipPreview?: boolean) => void;
  onEdit?: (scenarioId: string) => void;
  onDelete?: (scenarioId: string) => Promise<string>;
  tabIndex?: number;
  anonymous?: boolean;
}

export default function ScenarioCard(props: SenarioCardProps) {
  const { name, tags, id, onStart, onEdit, onDelete, anonymous } = props;
  const { getFormattedString, APFormattedMessage } = useVocabIntl();
  const [deletingScenario, setDeletingScenario] = useState<boolean>(false);
  const [deleteConfirmModalOpened, setDeleteConfirmModalOpened] = useState<boolean>(false);

  return (
    <Box sx={{
      bgColor: "rgba(81, 182, 194, 0.15)",
      p: 2,
      borderRadius: 12,
      height: "fit-content",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start"
    }}>
      <APCommonDialog
        open={deleteConfirmModalOpened}
        title={getFormattedString('delete-exercise-title')}
        onNext={() => {
          if (onDelete) {
            setDeletingScenario(true);
            onDelete(id)
              .then(() => {
                setDeleteConfirmModalOpened(false);
                setDeletingScenario(false);
              })
              .catch(() => {
                setDeleteConfirmModalOpened(false);
                setDeletingScenario(false);
              })
          }
        }}
        onClose={() => setDeleteConfirmModalOpened(false)}
        error
        btnLoading={deletingScenario}
        btnTitle={getFormattedString('delete-exercise-button')}
      >
        <Stack sx={{ background: "white", p: "2.5rem" }}>
          <Text>
            <APFormattedMessage id={"delete-exercise-text"} />
          </Text>
        </Stack>
      </APCommonDialog>
      <HStack alignItems={anonymous ? "flex-start" : "center"}>
        <APIconButton fontSize={"xl"}
                      tabIndex={props.tabIndex}
                      autoFocus={props.tabIndex === 1}
                      onClick={() => onStart(id, true)}
                      aria-label={`${getFormattedString('aria-label-start-primaire')} ${name}`}
        >
          <Play />
        </APIconButton>

        <Flex flexDirection={"column"} flexGrow={1}>
          <Text
            fontSize={"0.875rem"}
            fontWeight={500}
            color="black"
            lineHeight={1.5}
            sx={{ mt: anonymous ? '4px' : 0 }}
          >
            {name}
          </Text>
          <Text
            fontSize={"0.75rem"}
            fontWeight={400}
            color="black"
            lineHeight={1}
          >
            {tags.join(", ")}
          </Text>
          {anonymous && (
            <Text
              fontSize={"0.75rem"}
              fontWeight={400}
              color="#F95928"
              lineHeight={1}
              sx={{ my: '8px' }}
            >
              <APFormattedMessage id={"exercise-anonymous-label"} />
            </Text>
          )}
        </Flex>
        <VStack sx={{ marginLeft: "auto" }} alignSelf={"center"}>
          {onEdit && (
            <APIconButton fontSize={"2xl"}
                          variant={"solid"}
                          aria-label={`${getFormattedString('aria-label-edit-list')} ${name}`}
                          sx={{
                            background: "#51B6C233",
                            paddingInlineStart: 0,
                            paddingInlineEnd: 0,
                            padding: "0.5rem"
                          }}
                          onClick={() => onEdit(id)}
                          tabIndex={props.tabIndex && props.tabIndex + 1}
            >
              <FiEdit size={"1em"} color="#51B6C2" />
            </APIconButton>
          )}
          {onDelete && (
            <APIconButton aria-label={`${getFormattedString('aria-label-delete-list')} ${name}`}
                          fontSize={"2xl"}
                          variant={"solid"}
                          sx={{
                            background: "#51B6C233",
                            paddingInlineStart: 0,
                            paddingInlineEnd: 0,
                            padding: "0.5rem"
                          }}
                          onClick={() => setDeleteConfirmModalOpened(true)}
                          tabIndex={props.tabIndex && props.tabIndex + (onEdit ? 1 : 2)}
            >
              <FiTrash size={"1em"} color="#51B6C2" />
            </APIconButton>
          )}
        </VStack>
      </HStack>
    </Box>
  );
}
